export const META_PARTNERSHIP_ADS_STATE = Object.freeze({
  REQUESTED: "requested",
  SUBMITTED: "submitted",
  APPROVED: "approved",
  REJECTED: "rejected",
  NOT_INTERESTED: "not_interested",
} as const);

export type MetaPartnershipAdsStateValue =
  (typeof META_PARTNERSHIP_ADS_STATE)[keyof typeof META_PARTNERSHIP_ADS_STATE];

/**
 * Review decisions a brand may make, keyed by the item's current state (both
 * the backend and brand UI read this map).
 */
export const META_PARTNERSHIP_ADS_BRAND_TRANSITIONS: Partial<
  Record<MetaPartnershipAdsStateValue, readonly MetaPartnershipAdsStateValue[]>
> = {
  [META_PARTNERSHIP_ADS_STATE.REQUESTED]: [META_PARTNERSHIP_ADS_STATE.REJECTED],
  [META_PARTNERSHIP_ADS_STATE.SUBMITTED]: [
    META_PARTNERSHIP_ADS_STATE.APPROVED,
    META_PARTNERSHIP_ADS_STATE.REJECTED,
  ],
  [META_PARTNERSHIP_ADS_STATE.APPROVED]: [META_PARTNERSHIP_ADS_STATE.REJECTED],
  [META_PARTNERSHIP_ADS_STATE.REJECTED]: [META_PARTNERSHIP_ADS_STATE.APPROVED],
};

/** Whether the brand has approved or rejected the item; influencers can no longer change it. */
export function isPartnershipAdReviewed(
  state: MetaPartnershipAdsStateValue
): boolean {
  return (
    state === META_PARTNERSHIP_ADS_STATE.APPROVED ||
    state === META_PARTNERSHIP_ADS_STATE.REJECTED
  );
}

/** A request the brand rejected before the influencer submitted an ad code. */
export function isCancelledPartnershipAdRequest({
  state,
  adCode,
}: {
  state: MetaPartnershipAdsStateValue;
  adCode: string | null | undefined;
}): boolean {
  return state === META_PARTNERSHIP_ADS_STATE.REJECTED && !adCode;
}

export function canBrandReviewPartnershipAd(
  from: MetaPartnershipAdsStateValue,
  to: MetaPartnershipAdsStateValue,
  adCode: string | null | undefined
): boolean {
  // A decision can only be reversed while there is an ad code to act on.
  if (isPartnershipAdReviewed(from) && !adCode) {
    return false;
  }

  return META_PARTNERSHIP_ADS_BRAND_TRANSITIONS[from]?.includes(to) ?? false;
}
